import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {useSnackbar} from 'notistack';

import {useApplicationContext} from 'ApplicationContext';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';
import {apiErrorMessage} from 'Pages/Calendar/errorText';

import {LabelTask} from './labelTaskTypes';
import {
    LabelwinCustomer,
    LabelwinSystem,
    customerLabel,
    useCustomerSearch,
    useLookup,
} from './labelwinMasterData';

interface Props {
    /** Die Aufgabe, aus der ein Auftrag werden soll; `null` = Dialog zu. */
    task: LabelTask | null;
    onClose: () => void;
    /** Nach erfolgreicher Umwandlung — die Liste lädt neu. */
    onCreated: (task: LabelTask) => void;
}

/**
 * Aus einer Aufgabe einen Kundendienst-Auftrag machen.
 *
 * Der Schritt, der die Arbeit **abrechenbar** macht: Zeit wird in Labelwin immer auf einen Auftrag
 * gebucht, nie auf eine Aufgabe. Betreff, Text, Ansprechpartner und der Zuständige kommen aus der
 * Aufgabe — hier wird nur ergänzt, was ein Auftrag zusätzlich braucht.
 *
 * Der Kunde ist Pflicht. Trägt die Aufgabe schon einen, steht er fest; sonst wird er hier gesucht.
 */
export const TaskOrderDialog: React.FC<Props> = ({task, onClose, onCreated}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const orderTypes = useLookup(context.api, 'OrderTypes');
    const priorities = useLookup(context.api, 'Priorities');

    const [orderTypeId, setOrderTypeId] = useState<number | ''>('');
    const [priorityId, setPriorityId] = useState<number | ''>('');
    const [customer, setCustomer] = useState<LabelwinCustomer | null>(null);
    const [customerTerm, setCustomerTerm] = useState('');
    const [systems, setSystems] = useState<LabelwinSystem[]>([]);
    const [supportId, setSupportId] = useState('');
    const [busy, setBusy] = useState(false);

    const {customers, loading: searching} = useCustomerSearch(context.api, customerTerm);

    /** Die Aufgabe bringt den Kunden schon mit — dann ist hier nichts zu wählen. */
    const customerFromTask = task?.customerId ?? null;
    const customerId = customerFromTask ?? customer?.customerId ?? null;

    useEffect(() => {
        if (task === null) return;
        setOrderTypeId('');
        setPriorityId('');
        setCustomer(null);
        setCustomerTerm('');
        setSupportId('');
        setSystems([]);
    }, [task]);

    // Anlagen des Kunden für die optionale Zuordnung. Ohne Kunde gibt es nichts zu holen.
    useEffect(() => {
        if (customerId === null) {
            setSystems([]);
            return;
        }

        let active = true;
        http.get<LabelwinSystem[]>(`/Labelwin/Customers/${customerId}/Systems`)
            .then((r) => { if (active) setSystems(r.data); })
            // Still: ohne Liste bleibt die Anlage leer, der Auftrag entsteht trotzdem.
            .catch(() => { if (active) setSystems([]); });

        return () => { active = false; };
    }, [http, customerId]);

    const create = async (): Promise<void> => {
        if (task === null) return;
        setBusy(true);
        try {
            const r = await http.post<LabelTask>(`/Tasks/${task.id}/order`, undefined, {
                customerId: customerFromTask === null ? customer?.customerId ?? null : null,
                orderTypeId: orderTypeId === '' ? null : orderTypeId,
                priorityId: priorityId === '' ? null : priorityId,
                supportId: supportId || null,
            });
            enqueueSnackbar(
                t('labelTasks.orderCreated', {order: r.data.orderNumber ?? r.data.orderId}),
                {variant: 'success'});
            onCreated(r.data);
            onClose();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('labelTasks.orderFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={task !== null} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t('labelTasks.orderTitle')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <Typography variant="body2" color="text.secondary">
                        {task?.subject}
                    </Typography>

                    {customerFromTask !== null ? (
                        <TextField fullWidth size="small" label={t('labelTasks.field.customer')}
                                   value={[task?.customerName, task?.customerCity]
                                       .filter(Boolean).join(', ') || customerFromTask}
                                   disabled/>
                    ) : (
                        <Autocomplete
                            size="small"
                            options={customers}
                            value={customer}
                            loading={searching}
                            onChange={(_, value) => setCustomer(value)}
                            onInputChange={(_, value) => setCustomerTerm(value)}
                            getOptionLabel={customerLabel}
                            isOptionEqualToValue={(a, b) => a.customerId === b.customerId}
                            // Die Suche macht der Server; hier nichts zusätzlich wegfiltern.
                            filterOptions={(options) => options}
                            noOptionsText={t('labelTasks.customerHint')}
                            loadingText={t('labelTasks.searching')}
                            renderInput={(params) => (
                                <TextField {...params} autoFocus
                                           label={t('labelTasks.field.customer')}
                                           helperText={t('labelTasks.customerHint')}/>
                            )}
                        />
                    )}

                    {orderTypes.length > 0 && (
                        <TextField select fullWidth size="small" label={t('labelTasks.field.orderType')}
                                   value={orderTypeId}
                                   onChange={(e) => setOrderTypeId(
                                       e.target.value === '' ? '' : Number(e.target.value))}
                                   helperText={t('labelTasks.defaultHint')}>
                            <MenuItem value="">{t('labelTasks.useDefault')}</MenuItem>
                            {orderTypes.map((value) => (
                                <MenuItem key={value.id} value={value.id}>{value.name}</MenuItem>
                            ))}
                        </TextField>
                    )}

                    {priorities.length > 0 && (
                        <TextField select fullWidth size="small" label={t('labelTasks.field.priorityList')}
                                   value={priorityId}
                                   onChange={(e) => setPriorityId(
                                       e.target.value === '' ? '' : Number(e.target.value))}>
                            <MenuItem value="">{t('labelTasks.useDefault')}</MenuItem>
                            {priorities.map((value) => (
                                <MenuItem key={value.id} value={value.id}>{value.name}</MenuItem>
                            ))}
                        </TextField>
                    )}

                    {systems.length > 0 && (
                        <TextField select fullWidth size="small" label={t('labelTasks.field.system')}
                                   value={supportId}
                                   onChange={(e) => setSupportId(e.target.value)}>
                            <MenuItem value="">{t('labelTasks.noSystem')}</MenuItem>
                            {systems
                                .filter((system) => system.supportId)
                                .map((system) => (
                                    <MenuItem key={system.systemId} value={system.supportId!}>
                                        {[system.name, system.supportId].filter(Boolean).join(' · ')}
                                    </MenuItem>
                                ))}
                        </TextField>
                    )}

                    <Alert severity="info">{t('labelTasks.orderHint')}</Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>{t('labelTasks.cancel')}</Button>
                <Button variant="contained" onClick={() => void create()}
                        disabled={busy || customerId === null}>
                    {t('labelTasks.createOrder')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskOrderDialog;
